import { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts';
import { MvpBanner } from '@/components/MvpBanner';

interface ProtectedRouteProps {
  children: ReactNode;
}

export function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { isAuthenticated, isLoading, onboardingComplete, isAdmin, mfaPending } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Chargement...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // MFA pending — redirect to login for 2FA verification
  if (mfaPending) {
    return <Navigate to="/login" state={{ from: location, mfa: true }} replace />;
  }

  // Wait for onboarding check to complete
  if (onboardingComplete === null) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Chargement...</p>
        </div>
      </div>
    );
  }

  // Redirect to onboarding if not completed (e.g. Google user who hasn't done onboarding)
  if (onboardingComplete === false) {
    return <Navigate to="/register" replace />;
  }

  // Redirect admins to choice page on first visit after login
  if (isAdmin && !sessionStorage.getItem('clevia_admin_chose')) {
    sessionStorage.setItem('clevia_admin_chose', '1');
    return <Navigate to="/admin/choice" replace />;
  }

  return (
    <>
      <MvpBanner />
      {children}
    </>
  );
}
